/* Jumpfly celebration. Lottie player license: vendor-lottie-web-license.txt. */
(() => {
  if (customElements.get('jf-celebration')) return;
  let playerPromise;
  const player = (url) => {
    if (window.lottie?.loadAnimation) return Promise.resolve(window.lottie);
    if (!playerPromise) {
      playerPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.onload = () => window.lottie?.loadAnimation ? resolve(window.lottie) : reject(new Error('Lottie unavailable'));
        script.onerror = () => reject(new Error('Lottie failed to load'));
        document.head.append(script);
      }).catch((error) => { playerPromise = null; throw error; });
    }
    return playerPromise;
  };
  const fixed = (k) => ({ a: 0, k });
  const animated = (frames) => ({ a: 1, k: frames.map(([t, s], i) => {
    const frame = { t, s };
    if (frames[i + 1]) Object.assign(frame, { e: frames[i + 1][1], i: { x: [0.67], y: [1] }, o: { x: [0.33], y: [0] } });
    return frame;
  }) });
  const group = (shape, color) => [{ ty: 'gr', it: [shape,
    { ty: 'fl', c: fixed(color), o: fixed(100), r: 1, bm: 0 },
    { ty: 'tr', p: fixed([0, 0]), a: fixed([0, 0]), s: fixed([100, 100]), r: fixed(0), o: fixed(100), sk: fixed(0), sa: fixed(0) }
  ] }];
  function animationData(width, height, density) {
    const mobile = width < 750;
    const count = density === 'festive' ? (mobile ? 12 : 24) : (mobile ? 8 : 16);
    const colors = [[1, 0.443, 0.043, 1], [1, 0.749, 0.188, 1], [1, 0.957, 0.82, 1]];
    let seed = 1397;
    const random = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
    const layers = [];
    for (let i = 0; i < count; i++) {
      const right = i % 2 === 1;
      const start = (Math.floor(i / 2) % 6) * 12 + Math.floor(random() * 9);
      const duration = 68 + Math.floor(random() * 24);
      const x1 = (mobile ? 0.035 + random() * 0.055 : 0.12 + random() * 0.1);
      const x2 = Math.min(mobile ? 0.12 : 0.29, x1 + (mobile ? 0.025 : 0.07) + random() * 0.035);
      const flip = (x) => (right ? 1 - x : x) * width;
      const y = height * ((mobile ? 0.27 : 0.28) + random() * 0.1);
      const paperWidth = mobile ? 3 + random() * 2 : 5 + random() * 3;
      const paperHeight = paperWidth * (1.4 + random() * 0.7);
      const angle = random() * 180;
      layers.push({ ddd: 0, ind: i + 1, ty: 4, nm: 'Jumpfly confetti ' + i, sr: 1,
        ks: {
          o: animated([[start, [0]], [start + 8, [88]], [start + duration - 20, [70]], [start + duration, [0]]]),
          p: animated([[start, [flip(x1), y, 0]], [start + 23, [flip(x2), Math.max(35, y - height * 0.1), 0]], [start + duration, [flip(x2 - 0.02), height * (0.7 + random() * 0.13), 0]]]),
          a: fixed([0, 0, 0]),
          s: animated([[start, [55, 55, 100]], [start + 14, [100, 100, 100]], [start + 38, [24, 100, 100]], [start + 61, [100, 100, 100]], [start + duration, [35, 75, 100]]]),
          r: animated([[start, [angle]], [start + duration, [angle + (right ? -270 : 270)]]])
        },
        shapes: group({ ty: 'rc', d: 1, s: fixed([paperWidth, paperHeight]), p: fixed([0, 0]), r: fixed(1) }, colors[i % colors.length]),
        ip: start, op: start + duration, st: 0, bm: 0
      });
    }
    for (let i = 0; i < (mobile ? 2 : 4); i++) {
      const radius = mobile ? 6 : 10;
      const points = [[0, -radius], [radius * 0.22, -radius * 0.22], [radius, 0], [radius * 0.22, radius * 0.22], [0, radius], [-radius * 0.22, radius * 0.22], [-radius, 0], [-radius * 0.22, -radius * 0.22]];
      const start = 18 + i * 42;
      const x = i % 2 ? (mobile ? 0.94 : 0.77) : (mobile ? 0.06 : 0.23);
      layers.push({ ddd: 0, ind: count + i + 1, ty: 4, nm: 'Jumpfly sparkle ' + i, sr: 1,
        ks: { o: animated([[start, [0]], [start + 19, [88]], [start + 46, [0]]]), p: fixed([width * x, height * (0.27 + (i > 1 ? 0.28 : 0)), 0]), a: fixed([0, 0, 0]), s: animated([[start, [30, 30, 100]], [start + 20, [100, 100, 100]], [start + 46, [45, 45, 100]]]), r: fixed(12) },
        shapes: group({ ty: 'sh', ks: fixed({ i: points.map(() => [0, 0]), o: points.map(() => [0, 0]), v: points, c: true }) }, colors[1]), ip: start, op: start + 46, st: 0, bm: 0
      });
    }
    return { v: '5.13.0', fr: 30, ip: 0, op: 240, w: width, h: height, nm: 'Jumpfly Black Friday celebration', ddd: 0, assets: [], layers };
  }
  class Celebration extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._generation = 0;
      this._motion = matchMedia('(prefers-reduced-motion: reduce)');
      this._sync = this._sync.bind(this);
      this._motion.addEventListener('change', this._sync);
      document.addEventListener('visibilitychange', this._sync);
      this._intersection = new IntersectionObserver(([entry]) => { this._visible = entry.isIntersecting; this._sync(); }, { threshold: 0.01 });
      this._intersection.observe(this);
      this._resize = new ResizeObserver(() => {
        const size = Math.round(this.clientWidth) + 'x' + Math.round(this.clientHeight);
        if (size === this._size) return;
        this._size = size;
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => { this._animation?.destroy(); this._animation = null; this._sync(); }, 160);
      });
      this._resize.observe(this);
      this._sync();
    }
    disconnectedCallback() {
      this._connected = false;
      this._generation++;
      this._intersection?.disconnect();
      this._resize?.disconnect();
      this._motion?.removeEventListener('change', this._sync);
      document.removeEventListener('visibilitychange', this._sync);
      clearTimeout(this._resizeTimer);
      this._animation?.destroy();
      this._animation = null;
    }
    _sync() {
      if (!this.isConnected) return;
      if (this._motion.matches) {
        this._animation?.destroy();
        this._animation = null;
        this.dataset.state = 'reduced';
        return;
      }
      const active = this._visible && !document.hidden;
      if (!active) { this._animation?.pause(); this.dataset.state = 'paused'; return; }
      if (this._animation) { this._animation.play(); this.dataset.state = 'playing'; }
      else this._build();
    }
    async _build() {
      if (this._building || !this.clientWidth || !this.clientHeight) return;
      this._building = true;
      const generation = this._generation;
      this.dataset.state = 'loading';
      try {
        const lottie = await player(this.dataset.player);
        if (!this.isConnected || generation !== this._generation || this._motion.matches) return;
        const data = animationData(Math.round(this.clientWidth), Math.round(this.clientHeight), this.dataset.density);
        this.dataset.layerCount = data.layers.length;
        this._animation = lottie.loadAnimation({ container: this, renderer: 'svg', loop: true, autoplay: false, animationData: data, rendererSettings: { preserveAspectRatio: 'xMidYMid meet', progressiveLoad: true } });
        this._animation.setSubframe(false);
        this._animation.addEventListener('DOMLoaded', () => { this.querySelector('svg')?.setAttribute('focusable', 'false'); this._sync(); });
        this._sync();
      } catch { this.dataset.state = 'error'; }
      finally { this._building = false; }
    }
  }
  customElements.define('jf-celebration', Celebration);
})();
